import { useState } from 'react';
import { getApiErrorMessage, giftCardsAPI } from '@matix/api-client';
import { roundMoney } from '@matix/utils';
import { useGiftCards } from '../../store/giftCards';
import { moneyPrecision } from '../../store/currency';
import { toast } from '../../store/toast';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { CheckIcon, TrashIcon } from '../../components/icons';

/** One gift card the cashier has applied to this order. */
export interface AppliedGiftCard {
  cardNumber: string;
  masked: string;
  pin: string | null;
  amount: number;
  /** What the card had before this order — shown so the customer can be told. */
  balanceBefore: number;
}

/**
 * Gift-card tender, inside the payment modal.
 *
 * The cashier types (or scans) a card number, its PIN if the tenant requires
 * one, and how much of it to put on this order. Nothing here computes a
 * balance: the browser only ever displays a figure the API returned, and the
 * same card is re-validated under lock when the order is charged. That is what
 * keeps two terminals from spending the same balance.
 *
 * Applied cards are handed back to the payment modal, which turns their total
 * into a single payment split on the tenant's gift-card code. The individual
 * cards ride along in `gift_cards.redeem[]`, and the server refuses the order
 * unless the two add up to the same figure.
 *
 * Hidden entirely unless the tenant has gift cards on with a tender configured —
 * a till without them looks exactly as it did before this feature existed.
 */
export function GiftCardPayPanel({
  applied,
  onApply,
  onRemove,
  remaining,
  disabled = false,
}: {
  applied: AppliedGiftCard[];
  onApply: (card: AppliedGiftCard) => void;
  onRemove: (cardNumber: string) => void;
  /** What is still owed on the sale leg — the ceiling for one more card. */
  remaining: number;
  disabled?: boolean;
}): JSX.Element | null {
  const { t: tr } = useLang();
  const config = useGiftCards((s) => s.config);
  const canRedeem = useGiftCards((s) => s.canRedeem);

  const [cardNumber, setCardNumber] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * A card issued while the tenant still required PINs keeps its PIN even after
   * the tenant switches the requirement off — someone is holding a card that
   * says otherwise, and the server still demands it. So the PIN field appears
   * either because the tenant requires one, or because a card just asked for it.
   */
  const [pinAsked, setPinAsked] = useState(false);
  /** The card just looked up, waiting for the cashier to confirm an amount. */
  const [found, setFound] = useState<{
    masked: string;
    balance: number;
    expiresAt: string | null;
  } | null>(null);
  const [amountStr, setAmountStr] = useState('');

  if (!canRedeem()) return null;

  const precision = moneyPrecision();
  const rm = (n: number): number => roundMoney(n, precision);

  async function lookUp(): Promise<void> {
    const number = cardNumber.trim();
    if (!number) {
      toast(tr({ en: 'Enter or scan a card number', ar: 'أدخل أو امسح رقم البطاقة' }), 'warning');
      return;
    }
    if (applied.some((a) => a.cardNumber.toUpperCase() === number.toUpperCase())) {
      toast(tr({ en: 'That card is already applied', ar: 'هذه البطاقة مطبقة بالفعل' }), 'info');
      return;
    }
    setBusy(true);
    try {
      const res = await giftCardsAPI.lookup({
        card_number: number,
        ...(pin.trim() ? { pin: pin.trim() } : {}),
      });
      if (!res.ok) {
        toast(reasonText(res.reason), 'warning');
        // A PIN prompt is not a failure — reveal the field (if it was hidden)
        // and keep the number so the cashier only types the missing half.
        if (res.reason === 'GIFT_CARD_PIN_REQUIRED') setPinAsked(true);
        else setFound(null);
        return;
      }
      setFound({ masked: res.card_number_masked, balance: res.balance, expiresAt: res.expires_at });
      // Default to whichever is smaller: what is left on the card, or what is
      // left to pay. That is the amount the cashier wants nine times in ten.
      setAmountStr(String(rm(Math.min(res.balance, Math.max(0, remaining)))));
    } catch (err) {
      toast(getApiErrorMessage(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  function apply(): void {
    if (!found) return;
    const amount = rm(Number(amountStr) || 0);
    if (amount <= 0) {
      toast(tr({ en: 'Enter an amount to take off the card', ar: 'أدخل المبلغ المطلوب خصمه' }), 'warning');
      return;
    }
    if (amount > found.balance + 1e-9) {
      toast(
        tr({
          en: `The card only has ${fmt(found.balance)}`,
          ar: `رصيد البطاقة ${fmt(found.balance)} فقط`,
        }),
        'warning',
      );
      return;
    }
    if (amount > remaining + 1e-9) {
      toast(
        tr({
          en: `Only ${fmt(remaining)} is left to pay`,
          ar: `المتبقي للدفع ${fmt(remaining)} فقط`,
        }),
        'warning',
      );
      return;
    }
    if (
      config.max_cards_per_order != null &&
      applied.length >= config.max_cards_per_order
    ) {
      toast(
        tr({
          en: `At most ${config.max_cards_per_order} gift cards per order`,
          ar: `الحد الأقصى ${config.max_cards_per_order} بطاقة لكل طلب`,
        }),
        'warning',
      );
      return;
    }

    onApply({
      cardNumber: cardNumber.trim(),
      masked: found.masked,
      pin: pin.trim() || null,
      amount,
      balanceBefore: found.balance,
    });
    setCardNumber('');
    setPin('');
    setPinAsked(false);
    setFound(null);
    setAmountStr('');
  }

  /** Translate a server rejection code into something a cashier can act on. */
  function reasonText(reason: string | null): string {
    switch (reason) {
      case 'GIFT_CARD_NOT_FOUND':
        return tr({ en: 'No gift card with that number', ar: 'لا توجد بطاقة بهذا الرقم' });
      case 'GIFT_CARD_BLOCKED':
        return tr({ en: 'This card is blocked', ar: 'هذه البطاقة محظورة' });
      case 'GIFT_CARD_INACTIVE':
        return tr({ en: 'This card has not been activated yet', ar: 'لم يتم تفعيل هذه البطاقة بعد' });
      case 'GIFT_CARD_EXPIRED':
        return tr({ en: 'This card has expired', ar: 'انتهت صلاحية هذه البطاقة' });
      case 'GIFT_CARD_EMPTY':
        return tr({ en: 'This card has no balance left', ar: 'لا يوجد رصيد على هذه البطاقة' });
      case 'GIFT_CARD_PIN_REQUIRED':
        return tr({ en: 'Enter the card PIN', ar: 'أدخل الرمز السري للبطاقة' });
      case 'GIFT_CARD_PIN_INVALID':
        return tr({ en: 'That PIN is not correct', ar: 'الرمز السري غير صحيح' });
      default:
        return tr({ en: 'This card cannot be used', ar: 'لا يمكن استخدام هذه البطاقة' });
    }
  }

  const appliedTotal = rm(applied.reduce((s, a) => s + a.amount, 0));

  return (
    <div className="pay-giftcard">
      <div className="pay-section-title">{tr({ en: 'Gift card', ar: 'بطاقة هدايا' })}</div>

      {applied.map((a) => (
        <div key={a.cardNumber} className="pay-giftcard-applied">
          <span className="ops-mono">{a.masked}</span>
          <span>−{fmt(a.amount)}</span>
          <span className="pay-giftcard-left">
            {tr({ en: 'left', ar: 'المتبقي' })} {fmt(rm(a.balanceBefore - a.amount))}
          </span>
          <button
            type="button"
            className="pay-split-remove"
            onClick={() => onRemove(a.cardNumber)}
            disabled={disabled || busy}
            title={tr({ en: 'Remove card', ar: 'إزالة البطاقة' })}
          >
            <TrashIcon size={14} />
          </button>
        </div>
      ))}

      {applied.length > 1 && (
        <div className="pay-giftcard-total">
          {applied.length} {tr({ en: 'cards', ar: 'بطاقات' })} · {fmt(appliedTotal)}
        </div>
      )}

      {!found ? (
        <div className="pay-giftcard-row">
          <input
            className="pay-giftcard-input"
            placeholder={tr({ en: 'Card number', ar: 'رقم البطاقة' })}
            value={cardNumber}
            onChange={(e) => setCardNumber(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') void lookUp(); }}
            disabled={disabled || busy}
            autoComplete="off"
          />
          {(config.require_pin || pinAsked) && (
            <input
              className="pay-giftcard-pin"
              type="password"
              inputMode="numeric"
              placeholder={tr({ en: 'PIN', ar: 'الرمز' })}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void lookUp(); }}
              disabled={disabled || busy}
              autoComplete="off"
            />
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={() => void lookUp()}
            disabled={disabled || busy}
          >
            {busy ? tr({ en: 'Checking…', ar: 'جارٍ التحقق…' }) : tr({ en: 'Check', ar: 'تحقق' })}
          </button>
        </div>
      ) : (
        <div className="pay-giftcard-found">
          <div className="pay-giftcard-balance">
            <span className="ops-mono">{found.masked}</span>
            <strong>{fmt(found.balance)}</strong>
            {found.expiresAt && (
              <span className="pay-giftcard-left">
                {tr({ en: 'expires', ar: 'تنتهي' })}{' '}
                {new Date(found.expiresAt).toLocaleDateString()}
              </span>
            )}
          </div>
          <div className="pay-giftcard-row">
            <input
              className="pay-giftcard-input"
              type="number"
              min="0"
              step="0.01"
              value={amountStr}
              onChange={(e) => setAmountStr(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') apply(); }}
              disabled={disabled}
              autoFocus
            />
            <button type="button" className="btn-secondary" onClick={apply} disabled={disabled}>
              <CheckIcon size={14} />
              {tr({ en: 'Apply', ar: 'تطبيق' })}
            </button>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setFound(null); setAmountStr(''); }}
              disabled={disabled}
            >
              {tr({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
